import { useCallback, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

type CollapsibleSectionProps = {
  title: string;
  /** Trailing icon/element rendered on the right of the header (matches the
   *  existing section-card pattern where an icon sits opposite the title). */
  icon?: ReactNode;
  /** Optional element shown just left of the icon, e.g. a count badge. */
  rightElement?: ReactNode;
  defaultOpen?: boolean;
  /** When provided, the open/closed state is persisted in localStorage under
   *  this key. Useful in panels that remount on live refresh so a collapsed
   *  section doesn't pop back open. */
  storageKey?: string;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
};

const STORAGE_PREFIX = "collapsible-section:";

function readStoredOpen(storageKey: string | undefined, fallback: boolean): boolean {
  if (!storageKey) return fallback;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (raw === "0") return false;
    if (raw === "1") return true;
  } catch {
    /* ignore storage access errors (private mode, etc.) */
  }
  return fallback;
}

function writeStoredOpen(storageKey: string | undefined, open: boolean) {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, open ? "1" : "0");
  } catch {
    /* ignore storage access errors */
  }
}

/**
 * A compact collapsible card for narrow detail panels. Open state can persist
 * across live-refresh remounts when a storage key is provided.
 */
export default function CollapsibleSection({
  title,
  icon,
  rightElement,
  defaultOpen = true,
  storageKey,
  children,
  className = "",
  bodyClassName = "",
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(() => readStoredOpen(storageKey, defaultOpen));

  // `revealed` controls overflow: while collapsed or mid-animation we clip
  // (overflow-hidden) so the height transition works; once fully open we let
  // overflow show again so hover tooltips/badges aren't clipped at section edges.
  const [revealed, setRevealed] = useState(() => readStoredOpen(storageKey, defaultOpen));

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      writeStoredOpen(storageKey, next);
      if (!next) setRevealed(false);
      return next;
    });
  }, [storageKey]);

  return (
    <section className={`rounded-lg border bg-card ${className}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={`group flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left transition-colors hover:bg-muted/30 ${
          open ? "rounded-t-lg" : "rounded-lg"
        }`}
      >
        <div className="flex min-w-0 items-center gap-2">
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <h3 className="truncate text-sm font-medium text-foreground">
            {title}
          </h3>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {rightElement}
          <ChevronDown
            className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform duration-200 ${
              open ? "" : "-rotate-90"
            }`}
          />
        </div>
      </button>
      {/* Smooth height animation via CSS grid rows (0fr → 1fr) — no JS measuring.
          grid-cols-[minmax(0,1fr)] keeps the single column from growing past the
          container so long, unbreakable strings (URLs) truncate instead of overflowing. */}
      <div
        className={`grid grid-cols-[minmax(0,1fr)] transition-[grid-template-rows] duration-200 ease-in-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
        onTransitionEnd={(e) => {
          if (e.propertyName === "grid-template-rows" && open) setRevealed(true);
        }}
      >
        <div className={`min-w-0 min-h-0 ${revealed ? "" : "overflow-hidden"}`}>
          <div className={`border-t px-3.5 py-3 ${bodyClassName}`}>{children}</div>
        </div>
      </div>
    </section>
  );
}
